import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { NgbDropdownModule, NgbCollapseModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Backend } from 'src/app/backend-interface/backend';
import { WpFilters, WpObjectPromise } from 'src/app/service/global.service';
import { TranslationService } from 'src/app/service/translation.service';
import { BrowserPipe } from 'src/app/pipes/format.pipe';

@Component({
  selector: 'app-filters-ba',
  templateUrl: './filters-ba.component.html',
  styleUrls: ['./filters-ba.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatChipsModule,
    TranslateModule,
    NgbDropdownModule,
    NgbCollapseModule,
    NgbTooltipModule,
    BrowserPipe
  ],
  providers: [TranslationService]
})
export class FiltersBAComponent implements OnChanges {

  @Input() promiseResult?: WpObjectPromise;
  @Input() defaultFilter?: WpFilters;
  @Output() applyFilter = new EventEmitter<WpFilters>();

  isCollapsed = signal(false);
  searchmodel = signal('');
  formSdkVersions = this.formBuilder.group({});
  formApplications = this.formBuilder.group({});
  formOs = this.formBuilder.group({});
  formOsVersion = this.formBuilder.group({});
  formBrowser = this.formBuilder.group({});
  formBrowserVersion = this.formBuilder.group({});
  formSearch = this.formBuilder.group({
    id: false,
    userId: false,
    metadata: false
  });

  readonly translationService = inject(TranslationService);

  constructor(private readonly formBuilder: UntypedFormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
   
    if (changes && this.promiseResult?.results) {
      this.initMetadataForm();
      this.initFiltreSdkVersion();
      this.initFiltreApplications();
      this.initFiltreOs();
      this.initFiltreBrowser();
      this.isCollapsed.set(false);
      if (this.defaultFilter) {
        this.defaultFiltersPatch();
      }
    }
  }

  /**
   * Initialize all default forms
   */
  defaultFiltersPatch() {
    this.defaultPatchSdkVersion();
    this.defaultPatchApplications();
    this.defaultPatchOs();
    this.defaultPatchBrowser();
  }

  getSdkVersions() {
    return Backend.WP_FILTRES.getAllSdkVersions(this.promiseResult);
  }

  initFiltreSdkVersion() {
    Backend.FILTRES.initSimpleForm(this.getSdkVersions(), this.formSdkVersions);
  }

  defaultPatchSdkVersion() {
    this.defaultFilter?.sdkVersions?.forEach(sdk => {
      this.formSdkVersions.patchValue({ [sdk]: true });
    });
  }

  getApplications() {
    return Backend.WP_FILTRES.getAllApplication(this.promiseResult);
  }

  initFiltreApplications() {
    Backend.FILTRES.initSimpleForm(this.getApplications(), this.formApplications);
  }

  defaultPatchApplications() {
    this.defaultFilter?.applicationsNames?.forEach(app => {
      this.formApplications.patchValue({ [app]: true });
    });
    if (this.getApplications().length === 1) {
      this.formApplications.patchValue({ [this.getApplications()[0]]: true });
      this.formApplications.disable();
    }
  }

  initMetadataForm() {
    Backend.FILTRES.initMetadataSearchForm(true, this.formSearch);
  }

  getLKeysSearch() {
    return Object.keys(this.formSearch.controls);
  }

  /** patch selected categorie for input search (id, bwid, deviceid) */
  changeSelected(e: any, cat: string) {
    for (let c of this.getLKeysSearch()) {
      if (c === cat) {
        this.formSearch.patchValue({ [c]: e.selected });
      } else {
        this.formSearch.patchValue({ [c]: false });
      }
    }
  }

  getAllOsNames() {
    return Backend.WP_FILTRES.getAllOsNames(this.promiseResult);
  }

  initFiltreOs() {
    Backend.FILTRES.initFormWithSubForm(this.formOs, this.formOsVersion, this.getAllOsNames());
  }

  defaultPatchOs() {
    const osNamesAndVersions = this.defaultFilter?.osNamesAndVersions;
    if (!osNamesAndVersions) return;
    Object.keys(osNamesAndVersions).forEach(os => {
      this.formOs.patchValue({ [os]: true });
      this.initFormVersionOs(os);
      const formOsV = this.formOsVersion.get(os) as UntypedFormGroup;
      Object.keys(formOsV.controls).forEach(version => {
        const isVersionIncluded = osNamesAndVersions[os]?.includes(version);
        formOsV.patchValue({ [version]: !!isVersionIncluded });
      });
    });
  }

  initFormVersionOs(os: string) {
    const versions = Backend.WP_FILTRES.getAllVersionsForOs(this.promiseResult, os);
    Backend.FILTRES.initFormVersionsForValue(this.formOsVersion, versions, os);
  }

  getAllVersionsForOs(os: string) {
    return Backend.WP_FILTRES.getAllVersionsForOs(this.promiseResult, os);
  }

  getAllBrowserNames() {
    return Backend.WP_FILTRES.getAllBrowserNames(this.promiseResult);
  }

  getAllVersionsForBrowser(browser: string) {
    return Backend.WP_FILTRES.getAllVersionsForBrowser(this.promiseResult, browser);
  }

  initFiltreBrowser() {
    Backend.FILTRES.initFormWithSubForm(this.formBrowser, this.formBrowserVersion, this.getAllBrowserNames());
  }

  initFormVersionBrowser(browser: string) {
    const versions = this.getAllVersionsForBrowser(browser);
    Backend.FILTRES.initFormVersionsForValue(this.formBrowserVersion, versions, browser);
  }

  defaultPatchBrowser() {
    const browserNamesAndVersions = this.defaultFilter?.browserNamesAndVersions;
    if (!browserNamesAndVersions) return;
    Object.keys(browserNamesAndVersions).forEach(browser => {
      this.formBrowser.patchValue({ [browser]: true });
      this.initFormVersionBrowser(browser);
      const formBrowserV = this.formBrowserVersion.get(browser) as UntypedFormGroup;
      Object.keys(formBrowserV.controls).forEach(version => {
        const isVersionIncluded = browserNamesAndVersions[browser]?.includes(version);
        formBrowserV.patchValue({ [version]: !!isVersionIncluded });
      });
    });
  }

  resetSimpleForm(form: UntypedFormGroup) {
    Backend.FILTRES.resetSimpleForm(form);
  }

  resetFormAndSubForm(form: UntypedFormGroup, subForm: UntypedFormGroup) {
    Backend.FILTRES.resetFormAndSubForm(form, subForm);
  }

  someCompleteForValue(form: UntypedFormGroup, key: string) {
    return Backend.FILTRES.someComplete(form, key);
  }

  /**
   * It will check if the form for search an id is valid
   * 
   * @returns boolean
   */
  checkSearch() {
    const keys = Object.keys(this.formSearch.controls);
    const checked = keys.some(k => this.formSearch.get(k)?.value === true);
    return checked && this.searchmodel().trim() !== "" || this.searchmodel() === "";
  }

  initSearchTrue(type: string) {
    if (this.formSearch.get(type)?.value) {
      return this.searchmodel();
    } else {
      return undefined;
    }
  }

  statusMoreFilters() {
    return this.isCollapsed() ? 'remove' : 'add';
  }

  formatLongString(chaine: string | undefined, n: number) {
    return Backend.FORMAT.formatN(chaine, n);
  }

  /**
  * If all versions are uncheck for an sub-form it will uncheck value in the main form 
  * @param form
  * @param subForm
  * @param value
  */
  checkIfnoVersionInSubForm(form: UntypedFormGroup, subForm: UntypedFormGroup, value: string) {
    let no = Backend.FILTRES.checkIfnoValueTrue(subForm, value);
    if (no) {
      form.patchValue({ [value]: false });
      Backend.FILTRES.deleteFormVersion(subForm, value);
    }
  }

  /**
  * If all versions are check for an sub-form it will check value in the main form 
  * @param form
  * @param subForm
  * @param value
  */
  updateAllCompleteForm(form: UntypedFormGroup, subForm: UntypedFormGroup, value: string) {
    let all = Backend.FILTRES.updateAllComplete(subForm, value);
    if (all) {
      form.patchValue({ [value]: true });
    }
  }

  formatVersionFiltre(version: string) {
    return Backend.FORMAT.formatVersionCheckBox(version);
  }

  getVersionsTrue(form: UntypedFormGroup, formVersion: UntypedFormGroup) {
    let map: { [key: string]: any } = {};
    const listTrueValues = Backend.FILTRES.getAllFieldTrue(form);
    listTrueValues.forEach((value: string) => {
      map[value] = Backend.FILTRES.getAllFieldTrueForSpecificValue(formVersion, value);
    });
    return map;
  }

  applyFilters() {
    const filters: WpFilters = {
      sdkVersions: Backend.FILTRES.getAllFieldTrue(this.formSdkVersions),
      applicationsNames: Backend.FILTRES.getAllFieldTrue(this.formApplications),
      osNamesAndVersions: this.getVersionsTrue(this.formOs, this.formOsVersion),
      browserNamesAndVersions: this.getVersionsTrue(this.formBrowser, this.formBrowserVersion),
      id: this.initSearchTrue('id'),
      userId: this.initSearchTrue('userId'),
      metadata: this.initSearchTrue('metadata')
    };
    this.applyFilter.emit(filters);
  }
}
